import { lazy, Suspense, useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Plus,
  Newspaper,
  Copy,
  Check,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useConfirm } from '@/components/ui/confirm';
import { GenerateArticleDialog } from '@/components/blog/GenerateArticleDialog';
import {
  useBlogPosts,
  useBlogPost,
  useUpdateBlogPost,
  useRegenerateBlogPost,
  useDeleteBlogPost,
  type BlogCategory,
  type BlogPostRow,
} from '@/hooks/data/useBlog';
import { formatDate } from '@/lib/formatLocale';

// Lazy-load the markdown renderer so it doesn't land in the initial route chunk.
const ReactMarkdown = lazy(() => import('react-markdown'));

export const Route = createFileRoute('/_app/blog')({
  component: BlogPage,
});

const CATEGORIES: BlogCategory[] = ['buying', 'selling', 'investment', 'taxes', 'market'];
const CATEGORY_LABELS: Record<BlogCategory, string> = {
  buying: 'Kaufen',
  selling: 'Verkaufen',
  investment: 'Investment',
  taxes: 'Steuern',
  market: 'Markt',
};

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

function BlogPage() {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<BlogCategory | undefined>(undefined);

  const { data: posts = [], isLoading } = useBlogPosts({ category: categoryFilter });

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ink">{t('nav.blog', 'Blog')}</h2>
          <p className="text-sm text-ink-3">
            {t('blog.subtitle', 'KI-generierte Blog-Artikel für Ihre Immobilien-Website.')}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          {t('blog.new', 'Neuer Artikel')}
        </Button>
      </header>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        {/* ── Left: filter + list ── */}
        <div className="space-y-3">
          {/* Category filter chips */}
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setCategoryFilter(undefined)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                !categoryFilter
                  ? 'border-transparent bg-ink text-paper'
                  : 'border-line text-ink-3 hover:bg-paper-2'
              }`}
            >
              {t('common.all', 'Alle')}
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategoryFilter(c === categoryFilter ? undefined : c)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  categoryFilter === c
                    ? 'border-transparent bg-ink text-paper'
                    : 'border-line text-ink-3 hover:bg-paper-2'
                }`}
              >
                {t(`blog.cat_${c}`, CATEGORY_LABELS[c])}
              </button>
            ))}
          </div>

          {/* Posts list */}
          {isLoading && (
            <p className="text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
          )}
          {!isLoading && posts.length === 0 && (
            <div className="rounded-xl border border-dashed border-line p-6 text-center">
              <Newspaper className="mx-auto mb-2 h-6 w-6 text-ink-3" />
              <p className="text-sm text-ink-3">
                {t('blog.empty', 'Noch keine Artikel. Erstellen Sie Ihren ersten Blog-Artikel.')}
              </p>
            </div>
          )}
          {posts.map((post) => (
            <PostListItem
              key={post.id}
              post={post}
              selected={selectedId === post.id}
              onClick={() => setSelectedId(post.id)}
            />
          ))}
        </div>

        {/* ── Right: editor ── */}
        <div>
          {selectedId ? (
            <ArticleEditorLoader
              postId={selectedId}
              onDeleted={() => setSelectedId(null)}
            />
          ) : (
            <div className="rounded-xl border border-line p-8 text-center text-sm text-ink-3">
              {t('blog.empty_select', 'Wählen Sie links einen Artikel oder erstellen Sie einen neuen.')}
            </div>
          )}
        </div>
      </div>

      <GenerateArticleDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(id) => {
          setCreating(false);
          setSelectedId(id);
        }}
      />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Post list item
// ─────────────────────────────────────────────────────────────────────────────

function PostListItem({
  post,
  selected,
  onClick,
}: {
  post: BlogPostRow;
  selected: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();

  // created_at is typed as number but may arrive as ISO string at runtime —
  // always coerce via new Date() before formatting.
  const dateStr = formatDate(new Date(post.created_at));

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full flex-col gap-1 rounded-lg border p-3 text-start transition-colors ${
        selected ? 'border-ink bg-paper-2' : 'border-line hover:bg-paper-2'
      }`}
    >
      <span className="block truncate text-sm font-medium text-ink">{post.title}</span>
      <span className="flex flex-wrap items-center gap-1.5">
        <CategoryPill category={post.category} />
        <StatusPill status={post.status} />
        <span className="text-[11px] text-ink-3">{dateStr}</span>
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pills
// ─────────────────────────────────────────────────────────────────────────────

function CategoryPill({ category }: { category: BlogCategory }) {
  const { t } = useTranslation();
  return (
    <span className="rounded bg-paper-2 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-3">
      {t(`blog.cat_${category}`, CATEGORY_LABELS[category])}
    </span>
  );
}

function StatusPill({ status }: { status: 'draft' | 'published' }) {
  const { t } = useTranslation();
  const isDraft = status === 'draft';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        isDraft
          ? 'bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]'
          : 'bg-[hsl(var(--positive)/0.12)] text-[hsl(var(--positive))]'
      }`}
    >
      {isDraft
        ? t('blog.status_draft', 'Entwurf')
        : t('blog.status_published', 'Veröffentlicht')}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Loader wrapper — fetches single post then renders editor
// ─────────────────────────────────────────────────────────────────────────────

function ArticleEditorLoader({
  postId,
  onDeleted,
}: {
  postId: string;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const { data: post, isLoading } = useBlogPost(postId);

  if (isLoading) {
    return <p className="p-6 text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>;
  }
  if (!post) {
    return (
      <p className="p-6 text-sm text-ink-3">{t('common.not_found', 'Nicht gefunden.')}</p>
    );
  }

  return <ArticleEditor post={post} onDeleted={onDeleted} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Article editor
// ─────────────────────────────────────────────────────────────────────────────

type EditorTab = 'edit' | 'preview';

function ArticleEditor({
  post,
  onDeleted,
}: {
  post: BlogPostRow;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const update = useUpdateBlogPost();
  const regenerate = useRegenerateBlogPost();
  const del = useDeleteBlogPost();

  const [tab, setTab] = useState<EditorTab>('edit');
  const [title, setTitle] = useState(post.title);
  const [meta, setMeta] = useState(post.meta_description ?? '');
  const [keywords, setKeywords] = useState(post.keywords ?? '');
  const [body, setBody] = useState(post.body);
  const [copied, setCopied] = useState(false);

  // Re-sync when the underlying post changes (e.g. after regenerate).
  // Keyed on post.id + post.updated_at — same pattern as CampaignDetail ItemEditor.
  useEffect(() => {
    setTitle(post.title);
    setMeta(post.meta_description ?? '');
    setKeywords(post.keywords ?? '');
    setBody(post.body);
  }, [post.id, post.updated_at]);

  const dirty =
    title !== post.title ||
    meta !== (post.meta_description ?? '') ||
    keywords !== (post.keywords ?? '') ||
    body !== post.body;

  const save = async () => {
    try {
      await update.mutateAsync({
        id: post.id,
        title,
        metaDescription: meta || null,
        keywords: keywords || null,
        body,
      });
      toast.success(t('blog.saved', 'Gespeichert.'));
    } catch {
      toast.error(t('common.error', 'Etwas ist schiefgelaufen.'));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('blog.copy_failed', 'Kopieren nicht möglich.'));
    }
  };

  const handleRegenerate = async () => {
    const ok = await confirm({
      title: t('blog.regenerate', 'Neu generieren'),
      body: t(
        'blog.regenerate_confirm',
        'Artikel neu generieren? Der aktuelle Text wird ersetzt.',
      ),
      destructive: true,
    });
    if (!ok) return;
    try {
      await regenerate.mutateAsync(post.id);
      toast.success(t('blog.regenerated', 'Neu generiert.'));
    } catch {
      toast.error(t('blog.regenerate_failed', 'Generierung fehlgeschlagen.'));
    }
  };

  const handleStatusToggle = async () => {
    const newStatus = post.status === 'draft' ? 'published' : 'draft';
    try {
      await update.mutateAsync({ id: post.id, status: newStatus });
      toast.success(
        newStatus === 'published'
          ? t('blog.published', 'Veröffentlicht.')
          : t('blog.unpublished', 'Als Entwurf gespeichert.'),
      );
    } catch {
      toast.error(t('common.error', 'Etwas ist schiefgelaufen.'));
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('blog.delete_title', 'Artikel löschen?'),
      body: t('blog.delete_body', 'Dieser Artikel wird ausgeblendet.'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(post.id);
      onDeleted();
    } catch {
      toast.error(t('common.error', 'Etwas ist schiefgelaufen.'));
    }
  };

  const dateStr = formatDate(new Date(post.created_at));

  return (
    <div className="rounded-xl border border-line bg-card">
      {/* Editor header */}
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-medium text-ink">{post.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <CategoryPill category={post.category} />
            <StatusPill status={post.status} />
            <span className="text-[11px] text-ink-3">{dateStr}</span>
          </div>
        </div>

        {/* Bearbeiten / Vorschau toggle */}
        <div className="flex rounded-lg border border-line bg-paper-2 p-0.5">
          <button
            type="button"
            onClick={() => setTab('edit')}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              tab === 'edit' ? 'bg-paper text-ink shadow-sm' : 'text-ink-3 hover:text-ink-2'
            }`}
          >
            {t('blog.tab_edit', 'Bearbeiten')}
          </button>
          <button
            type="button"
            onClick={() => setTab('preview')}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              tab === 'preview' ? 'bg-paper text-ink shadow-sm' : 'text-ink-3 hover:text-ink-2'
            }`}
          >
            {t('blog.tab_preview', 'Vorschau')}
          </button>
        </div>
      </div>

      {/* Editor body */}
      <div className="space-y-3 p-4">
        {tab === 'edit' ? (
          <>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-3">
                {t('blog.field_title', 'Titel')}
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-3">
                {t('blog.field_meta', 'Meta-Beschreibung')}
              </label>
              <Input
                value={meta}
                onChange={(e) => setMeta(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-3">
                {t('blog.field_keywords', 'Keywords')}
              </label>
              <Input
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-3">
                {t('blog.field_body', 'Inhalt (Markdown)')}
              </label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={18}
                className="font-mono text-sm"
              />
            </div>
          </>
        ) : (
          <div className="prose prose-sm max-w-none min-h-[300px] rounded-lg border border-line bg-paper p-4">
            <Suspense fallback={<p className="text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>}>
              <ReactMarkdown>{body}</ReactMarkdown>
            </Suspense>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {/* Save — only shown when dirty */}
          {dirty && (
            <Button size="sm" onClick={save} disabled={update.isPending}>
              {t('blog.save', 'Speichern')}
            </Button>
          )}

          {/* Copy body */}
          <Button size="sm" variant="outline" onClick={copy}>
            {copied ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {t('blog.copy', 'Kopieren')}
          </Button>

          {/* Regenerate */}
          <Button
            size="sm"
            variant="outline"
            onClick={handleRegenerate}
            disabled={regenerate.isPending}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${regenerate.isPending ? 'animate-spin' : ''}`}
            />
            {t('blog.regenerate', 'Neu generieren')}
          </Button>

          {/* Status toggle */}
          <Button size="sm" variant="outline" onClick={handleStatusToggle} disabled={update.isPending}>
            {post.status === 'draft'
              ? t('blog.publish', 'Als veröffentlicht markieren')
              : t('blog.unpublish', 'Als Entwurf')}
          </Button>

          {/* Delete */}
          <Button
            size="sm"
            variant="outline"
            onClick={handleDelete}
            disabled={del.isPending}
            className="ms-auto text-[hsl(var(--danger))] hover:border-[hsl(var(--danger))]"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t('blog.delete', 'Löschen')}
          </Button>
        </div>
      </div>
    </div>
  );
}
